import { Head, router } from "@inertiajs/react"
import {
  MoreHorizontal,
  Plus,
  RotateCw,
  ShieldCheck,
  ShieldOff,
  Trash2,
  Users,
} from "lucide-react"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { ListPagination } from "@/components/admin/list-pagination"
import { ListShell } from "@/components/admin/list-shell"
import { InviteMemberDialog } from "@/components/brand/invite-member-dialog"
import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import { EmptyState } from "@/components/empty-state"
import { StatusBadge } from "@/components/status-badge"
import { TierBadge } from "@/components/tier-badge"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useMenuDialogHandoff } from "@/hooks/use-menu-dialog-handoff"
import { useOnline } from "@/hooks/use-online"
import AppLayout from "@/layouts/app-layout"
import { toAppLocale } from "@/lib/i18n/locales"
import { formatRelativeIntl } from "@/lib/relative-time"
import {
  brandMemberRolePath,
  brandSettingsTeamPath,
  invitationPath,
  resendInvitationPath,
} from "@/routes"
import type { BrandTeamIndexProps, BrandTeamRow } from "@/types/brand"

/**
 * Brand/Team/Index — interleaved list of accepted brand members +
 * pending/expired invitations for the current brand (OKS-14 PRD AC2).
 *
 * **Structurally identical to OKS-13's `Agency/Team/Index.tsx`** by
 * design — same single-table, same `kind` discriminator on row id,
 * same per-row server-authoritative `tenant_role` + `status`. Backend
 * shares the row serializer between agency + brand consoles; a
 * cross-page contract spec asserts the parity.
 *
 * Backend ships `rows[]` sorted alphabetically by `name` server-side
 * (using the email-localpart fallback for invitation rows). FE does
 * NOT re-sort — the `data-state` contract tests assert rendering keys
 * off props, not derivation.
 *
 * Two FE adaptations on top of the OKS-13 shape:
 *
 * 1. **Pending-row email rendering** (UX Spec §"Display-name handling"):
 *    when no User row exists yet, the row's `name` is the
 *    email-localpart fallback. The Email column visualisation
 *    italicises the email via `<span className="italic">` (NOT `<em>`
 *    — SR reads the email plainly without semantic emphasis). On
 *    acceptance, the row swaps to the User's actual name without
 *    changing alphabetical position (handled by backend's join key).
 *
 * 2. **`can_invite_member` server-driven gate** (BE6 boundary): both
 *    the toolbar trigger AND every row's action DropdownMenu are
 *    **absent from the DOM** for member-tier brand users — NOT
 *    show-and-disable. Per UX Spec §Accessibility, this prevents SR
 *    users discovering disabled affordances they can't trigger.
 *
 * Locked QA testid (OKS-14 v4 freeze):
 *   - `brand-team-list` (page region)
 *   - `brand-team-row[data-{member,invitation}-id]` per row
 *   - `brand-tier-badge[data-state]` per user row
 *   - `brand-status-badge[data-state]` per row
 *   - `brand-team-row-actions[data-invitation-id]` on each menu trigger
 *   - `brand-team-action-{resend,revoke-trigger}` on the menu items
 *   - `brand-team-revoke-confirm` on the destructive AlertDialog primary
 *   - `brand-invite-member-trigger` on the toolbar Button (admin only)
 */
export default function BrandTeamIndex({
  rows,
  pagination,
  can_invite_member,
  can_manage_role,
  admin_count,
}: BrandTeamIndexProps) {
  const { t } = useTranslation()

  const isEmpty = rows.length === 0
  // OKS-460 — the actions column shows when EITHER affordance can render
  // (invitation resend/revoke OR user role grant/revoke). Both are
  // admin-tier gates so they move together, but we OR them so the column
  // never disappears if the two ever diverge.
  const showActionsColumn = can_invite_member || can_manage_role

  const onPageChange = useCallback(
    (page: number) =>
      router.get(
        brandSettingsTeamPath(),
        { page },
        {
          preserveState: true,
          preserveScroll: true,
          only: ["rows", "pagination"],
        },
      ),
    [],
  )

  return (
    <AppLayout>
      <Head title={t("brand.team.page.title")} />
      {/* OKS-672 — Team page now renders inside the Brand Settings shell
          (settings heading + sub-nav aside + content), `wide` so the
          members/invitations data table keeps its legibility. Page
          contents below are unchanged from the standalone version. */}
      <BrandSettingsLayout wide>
        <ListShell
          testId="brand-team-list"
          title={t("brand.team.page.title")}
          subtitle={t("brand.team.page.subtitle", { count: pagination.total })}
          action={
            // BE6 gate — absent from the DOM (NOT show-and-disable) when
            // the current user is member-tier. See JSDoc above.
            can_invite_member ? (
              <InviteMemberDialog
                trigger={
                  <Button
                    type="button"
                    data-testid="brand-invite-member-trigger"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    {t("brand.team.page.invite_cta")}
                  </Button>
                }
              />
            ) : undefined
          }
          footer={
            isEmpty ? undefined : (
              <ListPagination
                page={pagination.page}
                perPage={pagination.per_page}
                total={pagination.total}
                onPageChange={onPageChange}
              />
            )
          }
        >
          {isEmpty ? (
            // Two empty-state copy variants per UX Spec §States: admin
            // gets the "Invite your first teammate" subtitle (no inline
            // CTA — the toolbar trigger above is the discoverable
            // affordance); member gets a single-line headline-only copy
            // with NO CTA at all (BE6 — affordance must be DOM-absent).
            <EmptyState
              icon={Users}
              title={
                can_invite_member
                  ? t("brand.team.empty.title_admin")
                  : t("brand.team.empty.title_member")
              }
              description={
                can_invite_member
                  ? t("brand.team.empty.subtitle_admin")
                  : undefined
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead aria-sort="ascending">
                    {t("brand.team.table.col_name")}
                  </TableHead>
                  <TableHead>{t("brand.team.table.col_email")}</TableHead>
                  <TableHead>{t("brand.team.table.col_tier")}</TableHead>
                  <TableHead>{t("brand.team.table.col_status")}</TableHead>
                  <TableHead>{t("brand.team.table.col_sent_at")}</TableHead>
                  {showActionsColumn && (
                    <TableHead className="text-right">
                      <span className="sr-only">
                        {t("brand.team.table.col_actions")}
                      </span>
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TeamRow
                    key={row.id}
                    row={row}
                    canManage={can_invite_member}
                    canManageRole={can_manage_role}
                    adminCount={admin_count}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </ListShell>
      </BrandSettingsLayout>
    </AppLayout>
  )
}

interface TeamRowProps {
  row: BrandTeamRow
  /**
   * Page-level admin gate. Used to suppress the entire actions cell —
   * member-tier viewers don't render the column at all (per BE6
   * DOM-absent contract). Even when `canManage === true`, individual
   * rows still consult `row.can_manage` for fine-grained gating
   * (e.g. invitation rows consult `row.can_manage`).
   */
  canManage: boolean
  /**
   * OKS-460 — page-level gate for the user-row role actions ("Make
   * admin" / "Remove admin"). Same DOM-absent contract: member-tier
   * viewers never see the menu on user rows.
   */
  canManageRole: boolean
  /** OKS-460 — live brand admin count; drives the sole-admin lock. */
  adminCount: number
}

/**
 * Single team row — discriminates on `row.kind` to choose the per-row
 * `data-{member,invitation}-id` attribute and decide whether to mount
 * the action `DropdownMenu`.
 *
 * Email column rendering — UX Spec §"Display-name handling":
 *   - `kind: "user"` → render `row.email` plain
 *   - `kind: "invitation"` → render `row.email` italicised via
 *     `<span className="italic">` (NOT `<em>` — SR reads plainly,
 *     UX Spec §Accessibility)
 *
 * Display-name column always renders `row.name` (server
 * always provides a non-empty value — actual User name for accepted
 * rows, email-localpart fallback for invitations).
 */
function TeamRow({ row, canManage, canManageRole, adminCount }: TeamRowProps) {
  const { t } = useTranslation()
  const isInvitation = row.kind === "invitation"
  const idAttr = isInvitation
    ? { "data-invitation-id": row.id }
    : { "data-member-id": row.id }
  const showActionsCell = canManage || canManageRole

  return (
    <TableRow data-testid="brand-team-row" {...idAttr}>
      <TableCell className="font-medium">{row.name}</TableCell>
      <TableCell className="text-muted-foreground">
        {isInvitation ? (
          <span className="italic" data-testid="brand-team-row-email-pending">
            {row.email}
          </span>
        ) : (
          row.email
        )}
      </TableCell>
      <TableCell>
        {/*
         * Tier rendering — three branches:
         *   - Invitation row: em-dash placeholder (UX-Lead OKS-13
         *     decision; tier informational at acceptance only).
         *   - User row with null `tenant_role` (legacy / pre-BE6
         *     account that hasn't been backfilled): same em-dash
         *     placeholder. Same convention as OKS-13's
         *     `Agency/Brands/Show` Team Card.
         *   - User row with non-null `tenant_role`: server-authoritative
         *     `<TierBadge>` keyed off the prop verbatim.
         */}
        {isInvitation || row.tenant_role === null ? (
          <span className="text-muted-foreground" aria-hidden="true">
            —
          </span>
        ) : (
          <TierBadge tier={row.tenant_role} testId="brand-tier-badge" />
        )}
      </TableCell>
      <TableCell>
        <StatusBadge status={row.status} testId="brand-status-badge" />
      </TableCell>
      <TableCell className="text-muted-foreground">
        {row.sent_at ? (
          <RelativeSentAt iso={row.sent_at} />
        ) : (
          <span aria-hidden="true">{t("brand.team.table.sent_at_dash")}</span>
        )}
      </TableCell>
      {showActionsCell && (
        <TableCell className="text-right">
          {isInvitation ? (
            canManage && row.can_manage ? (
              <RowActions row={row} />
            ) : null
          ) : canManageRole ? (
            <MemberRowActions row={row} adminCount={adminCount} />
          ) : null}
        </TableCell>
      )}
    </TableRow>
  )
}

/**
 * Row-actions dropdown. Mounts only for invitation rows where
 * `can_manage === true` (server-derived per row + page-level
 * `can_invite_member` ALSO true). Resend dispatches optimistic toast
 * + Inertia POST; Revoke opens an `<AlertDialog>` with Cancel
 * autofocused per Crew destructive convention.
 *
 * Same offline-tooltip pattern as OKS-13's Agency/Team RowActions:
 * `<span tabIndex={0}>` wrapper around the disabled trigger Button so
 * Radix Tooltip receives pointer/focus events (disabled `<button>`s
 * don't dispatch them).
 */
function RowActions({ row }: { row: BrandTeamRow }) {
  const { t } = useTranslation()
  const online = useOnline()
  const [revokeOpen, setRevokeOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()

  // Strip the `"invitation:"` prefix to get the raw numeric id for the
  // OKS-11 invitation routes (which expect an integer id, not the
  // namespaced row id).
  const numericId = Number(row.id.replace(/^invitation:/, ""))

  function handleResend() {
    if (submitting || !online) return
    toast.success(t("brand.toast.resent"))
    setSubmitting(true)
    router.post(
      resendInvitationPath({ id: numericId }),
      {},
      {
        preserveScroll: true,
        onError: () => toast.error(t("brand.errors.server")),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function handleRevoke() {
    if (submitting || !online) return
    setSubmitting(true)
    router.delete(invitationPath({ id: numericId }), {
      preserveScroll: true,
      onSuccess: () => {
        toast.success(t("brand.toast.revoked"))
        setRevokeOpen(false)
      },
      onError: () => toast.error(t("brand.errors.server")),
      onFinish: () => setSubmitting(false),
    })
  }

  const triggerButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={t("brand.team.actions.row_aria", { email: row.email })}
      data-testid="brand-team-row-actions"
      data-invitation-id={row.id}
      disabled={submitting || !online}
      aria-disabled={submitting || !online ? "true" : undefined}
    >
      <MoreHorizontal className="size-4" aria-hidden="true" />
    </Button>
  )

  return (
    <>
      {!online ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <span tabIndex={0} className="inline-flex">
                {triggerButton}
              </span>
            }
          />
          <TooltipContent>
            {t("brand.team.actions.offline_tooltip")}
          </TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger render={triggerButton} />
          <DropdownMenuContent align="end" finalFocus={finalFocus}>
            <DropdownMenuItem
              onClick={() => {
                handleResend()
              }}
              data-testid="brand-team-action-resend"
              aria-label={t("brand.team.actions.resend_aria", {
                email: row.email,
              })}
            >
              <RotateCw className="size-4" aria-hidden="true" />
              {t("brand.team.actions.resend")}
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() => {
                yieldFocusToDialog()
                setRevokeOpen(true)
              }}
              data-testid="brand-team-action-revoke-trigger"
              aria-label={t("brand.team.actions.revoke_aria", {
                email: row.email,
              })}
            >
              <Trash2 className="size-4" aria-hidden="true" />
              {t("brand.team.actions.revoke")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("brand.team.actions.revoke_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("brand.team.actions.revoke_confirm_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div
            className="text-foreground bg-muted/40 rounded-md border px-3 py-2 text-sm font-medium break-all"
            data-testid="brand-team-revoke-email"
          >
            {row.email}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>
              {t("brand.team.actions.revoke_confirm_cancel")}
            </AlertDialogCancel>
            {/* OKS-1023 — plain `Button`: `AlertDialogAction` is Base UI
                `Dialog.Close` and would close before the request settles. */}
            <Button
              type="button"
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={handleRevoke}
              disabled={submitting || !online}
              aria-disabled={submitting || !online ? "true" : undefined}
              data-testid="brand-team-revoke-confirm"
            >
              {t("brand.team.actions.revoke_confirm_submit")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/**
 * OKS-460 — Per-user role actions ("Make admin" / "Remove admin").
 * Mounts only on `kind: "user"` rows when the page-level
 * `can_manage_role` gate is true (member-tier viewers never reach it —
 * DOM-absent, UX Spec §Accessibility).
 *
 * Behaviour by current tier:
 *   - member → **"Make admin"**: direct `PATCH … { tenant_role: "admin" }`.
 *     No client toast — the success toast is server-driven via the
 *     use-flash → sonner bridge after the partial reload confirms
 *     (UX Spec §Micro-Interactions "optimistic-feeling").
 *   - admin → **"Remove admin"**: opens an `<AlertDialog>` confirm
 *     (Cancel autofocused, destructive primary). When the row is the
 *     SOLE admin (`adminCount <= 1`) the item is disabled + tooltip
 *     ("A brand must keep at least one admin") — stays present with
 *     `aria-disabled` so SR users hear the reason (UX Spec §A11y).
 *     The server enforces the same guardrail regardless.
 *
 * Self-demotion (`isSelf`) adds an extra "you'll lose access" warning
 * inside the confirm dialog. The server returns member-tier props on
 * the partial reload, so the actions disappear after a self-demote.
 *
 * Offline: the trigger is disabled + tooltipped, mirroring `RowActions`.
 */
function MemberRowActions({
  row,
  adminCount,
}: {
  row: BrandTeamRow
  adminCount: number
}) {
  const { t } = useTranslation()
  const online = useOnline()
  const { currentUser } = useCurrentContext()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()
  const [submitting, setSubmitting] = useState(false)

  // Strip the `"user:"` prefix → raw User id for `brandMemberRolePath`.
  const numericId = Number(row.id.replace(/^user:/, ""))
  const isAdmin = row.tenant_role === "admin"
  const isLastAdmin = isAdmin && adminCount <= 1
  const isSelf = currentUser?.id === numericId

  function patchRole(tenantRole: "admin" | "member", onDone?: () => void) {
    if (submitting || !online) return
    setSubmitting(true)
    router.patch(
      brandMemberRolePath({ id: numericId }),
      { tenant_role: tenantRole },
      {
        preserveScroll: true,
        onSuccess: () => onDone?.(),
        onError: () => toast.error(t("brand.errors.server")),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  const triggerButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={t("brand.team.actions.row_aria", { email: row.email })}
      data-testid="brand-team-member-actions"
      data-member-id={row.id}
      disabled={submitting || !online}
      aria-disabled={submitting || !online ? "true" : undefined}
    >
      <MoreHorizontal className="size-4" aria-hidden="true" />
    </Button>
  )

  return (
    <>
      {!online ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <span tabIndex={0} className="inline-flex">
                {triggerButton}
              </span>
            }
          />
          <TooltipContent>
            {t("brand.team.actions.offline_tooltip")}
          </TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger render={triggerButton} />
          <DropdownMenuContent align="end" finalFocus={finalFocus}>
            {!isAdmin ? (
              <DropdownMenuItem
                onClick={() => {
                  patchRole("admin")
                }}
                data-testid="brand-team-action-make-admin"
                aria-label={t("brand.team.actions.make_admin_aria", {
                  email: row.email,
                })}
              >
                <ShieldCheck className="size-4" aria-hidden="true" />
                {t("brand.team.actions.make_admin")}
              </DropdownMenuItem>
            ) : isLastAdmin ? (
              // Sole admin: present-but-disabled with an explanatory
              // tooltip (NOT DOM-absent) so the rule is discoverable +
              // SR-announced (UX Spec §A11y). `span` wrapper lets Radix
              // Tooltip receive events the disabled item won't dispatch.
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span tabIndex={0} className="inline-flex w-full">
                      <DropdownMenuItem
                        variant="destructive"
                        disabled
                        aria-disabled="true"
                        data-testid="brand-team-action-remove-admin"
                        data-last-admin="true"
                      >
                        <ShieldOff className="size-4" aria-hidden="true" />
                        {t("brand.team.actions.remove_admin")}
                      </DropdownMenuItem>
                    </span>
                  }
                />
                <TooltipContent>
                  {t("brand.team.actions.last_admin_tooltip")}
                </TooltipContent>
              </Tooltip>
            ) : (
              <DropdownMenuItem
                variant="destructive"
                onClick={() => {
                  yieldFocusToDialog()
                  setConfirmOpen(true)
                }}
                data-testid="brand-team-action-remove-admin"
                aria-label={t("brand.team.actions.remove_admin_aria", {
                  email: row.email,
                })}
              >
                <ShieldOff className="size-4" aria-hidden="true" />
                {t("brand.team.actions.remove_admin")}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("brand.team.actions.remove_admin_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("brand.team.actions.remove_admin_confirm_body", {
                name: row.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {isSelf && (
            <div
              className="text-destructive bg-destructive/10 rounded-md border px-3 py-2 text-sm font-medium"
              data-testid="brand-team-remove-admin-self-warning"
            >
              {t("brand.team.actions.remove_admin_confirm_self")}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              autoFocus
              data-testid="brand-team-remove-admin-cancel"
            >
              {t("brand.team.actions.remove_admin_confirm_cancel")}
            </AlertDialogCancel>
            {/* OKS-1023 — plain `Button`: `AlertDialogAction` is Base UI
                `Dialog.Close` and would close before the request settles. */}
            <Button
              type="button"
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={() => patchRole("member", () => setConfirmOpen(false))}
              disabled={submitting || !online}
              aria-disabled={submitting || !online ? "true" : undefined}
              data-testid="brand-team-remove-admin-confirm"
            >
              {t("brand.team.actions.remove_admin_confirm_submit")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/**
 * Localised relative sent-at label, via `formatRelativeIntl`.
 */
function RelativeSentAt({ iso }: { iso: string }) {
  const { t, i18n } = useTranslation()
  return (
    <span>
      {t("brand.team.table.sent_at_value", {
        relative: formatRelativeIntl(iso, toAppLocale(i18n.language)),
      })}
    </span>
  )
}
